import type { ReactNode } from 'react';
import type { FieldErrors, FieldValues } from 'react-hook-form';

import { Label } from '@/components/ui/label';

interface FieldShellProps {
  readonly id: string;
  readonly label: string;
  readonly error: string | undefined;
  // Renders the required marker. Driven by the shared field model's per-kind ×
  // per-side rules, so record (buy) and generate (sell) mark the same fields.
  readonly required?: boolean | undefined;
  readonly children: ReactNode;
}

export function FieldShell({ id, label, error, required, children }: FieldShellProps) {
  return (
    <div className="flex flex-col gap-1.5">
      {/* The marker stays outside the Label so label-text queries and the
          field's accessible name keep the bare label. */}
      <div className="flex items-baseline gap-0.5">
        <Label htmlFor={id}>{label}</Label>
        {required === true && (
          <span aria-hidden="true" className="text-destructive" data-testid={`required-${id}`}>
            *
          </span>
        )}
      </div>
      {children}
      {error !== undefined && (
        <p className="text-destructive text-sm" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}

export function errorMessage<TValues extends FieldValues>(
  errors: FieldErrors<TValues>,
  key: string,
): string | undefined {
  const node = (errors as Record<string, unknown>)[key];
  if (node !== null && typeof node === 'object' && 'message' in node) {
    const msg = (node as { message?: unknown }).message;
    if (typeof msg === 'string') return msg;
  }
  return undefined;
}
